/**
 * Idle Timeout Handler - Refactored
 * Logs out on inactivity.
 * Uses system-level idle detection when available (Teams-like behavior),
 * and falls back to tab activity detection otherwise.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import { IDLE_TIMEOUT_MINUTES } from '../utils/constants';

const IdleTimeoutHandler = () => {
  const { logout, user, isAuthenticated } = useAuth();
  const [useFallbackTimer, setUseFallbackTimer] = useState(true);
  const hasLoggedOutRef = useRef(false);

  // Get idle timeout from user data or use default
  const idleTimeMinutes = Number(user?.idleTime) || IDLE_TIMEOUT_MINUTES;
  const idleTimeMs = idleTimeMinutes * 60 * 1000;

  const logoutIfNeeded = useCallback(() => {
    if (!isAuthenticated || hasLoggedOutRef.current) {
      return;
    }

    hasLoggedOutRef.current = true;
    logout();
  }, [isAuthenticated, logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasLoggedOutRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let isCancelled = false;
    let abortController;

    const setupSystemIdleDetection = async () => {
      if (!isAuthenticated || typeof window === 'undefined' || !('IdleDetector' in window)) {
        setUseFallbackTimer(true);
        return;
      }

      try {
        // Permission API is Chromium-specific for IdleDetector.
        const permission = await window.IdleDetector.requestPermission();
        if (permission !== 'granted') {
          setUseFallbackTimer(true);
          return;
        }

        const detector = new window.IdleDetector();
        detector.addEventListener('change', () => {
          if (detector.userState === 'idle' || detector.screenState === 'locked') {
            logoutIfNeeded();
          }
        });

        abortController = new AbortController();
        await detector.start({
          threshold: idleTimeMs,
          signal: abortController.signal
        });

        if (!isCancelled) {
          setUseFallbackTimer(false);
        }
      } catch {
        if (!isCancelled) {
          setUseFallbackTimer(true);
        }
      }
    };

    setupSystemIdleDetection();

    return () => {
      isCancelled = true;
      if (abortController) {
        abortController.abort();
      }
    };
  }, [idleTimeMs, isAuthenticated, logoutIfNeeded]);

  useIdleTimer({
    onIdle: logoutIfNeeded,
    timeout: idleTimeMs,
    throttle: 500,
    disabled: !isAuthenticated || !useFallbackTimer,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove'
    ]
  });

  return null;
};

export default IdleTimeoutHandler;

